<template>
	<article
		class="section section--expanded section--floating section--full service">
		<div class="section">
			<header>
				<div class="flex gap-2 content-between">
					<div>
						<div class="flex gap-2 content-between">
							<div>
								<h3 class="beta heavy" v-html="instructionHeader(instruction)"/>
								<dl class="grid-list mt-1">
									<dt class="heavy">Created</dt>
									<dd v-html="$present('dateTime', instruction.created_at)"/>
									<dt class="heavy">Case reference</dt>
									<dd v-if="!instruction.internalReference">—</dd>
									<dd v-else v-html="instruction.internalReference"/>
									<template
										v-if="instruction.external_service_id || $can('pushToExternalService', instruction)">
										<dt class="heavy">External ID</dt>
										<dd v-if="!instruction.external_service_id">—</dd>
										<dd v-else v-html="instruction.external_service_id"/>
									</template>
									<dt class="heavy">Calm ID</dt>
									<dd v-html="instruction.id"/>
									<template v-if="instruction.address">
										<dt class="heavy">Address</dt>
										<dd>
											<Address
												:address="instruction.address"
												:display-county="false"
												:display-locality="false"/>
										</dd>
									</template>
									<dt class="heavy">Referrer</dt>
									<dd v-html="instruction.source?.profile?.name || instruction.metaReferrer[0]?.value || '—'"/>
								</dl>
							</div>
						</div>
					</div>
					<div class="flex flex-col gap-1 items-end">
						<div class="flex gap-1 items-center">
							<dl v-if="instruction.quote">
								<dt class="sr-only">Quote</dt>
								<dd class="beta heavy" v-html="$present('price', instruction.quote.calculation.total)"/>
							</dl>
							<status-badge :status="instruction.status" group="instruction"/>
							<v-contextual :processing="processing">
								<ul>
									<v-contextual-option
										v-if="$can('pushToExternalService', instruction)"
										@click="showPushToExternalServiceModal()">Push to External Service</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('accept', instruction)"
										@click="showAcceptModal()">Accept</v-contextual-option>
									<v-contextual-option
										v-if="$can('close', instruction)"
										@click="showCloseModal()">Close</v-contextual-option>
									<v-contextual-option
										v-if="$can('completeWithValue', instruction)"
										@click="showCompleteModal()">Complete</v-contextual-option>
									<v-contextual-option
										v-if="$can('completeInstantly', instruction)"
										@click="complete()">Complete</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('assign', instruction)"
										@click="showAssignModal()">Assign</v-contextual-option>
								</ul>
								<ul v-if="$can('view', instruction.quote)">
									<v-contextual-option
										@click="showQuoteModal(instruction.service, instruction.quote)">View Quote</v-contextual-option>
									<v-contextual-option
										@click="viewFile(instruction.quote.pdf)">View Quote PDF</v-contextual-option>
									<v-contextual-option
										v-if="$can('update', instruction.quote)"
										:route-to="{name: 'quotes.edit', params: {quote: instruction.quote.id}}">Edit Quote</v-contextual-option>
									<template v-if="$can('sendQuoteEmail', instruction)">
										<v-contextual-option
											@click.prevent="displayQuoteLinkModal()">Copy Quote Link</v-contextual-option>
										<v-contextual-option
											@click.prevent="displayQuoteLinkModal(true)">Send Quote Email</v-contextual-option>
									</template>
								</ul>
								<ul>
									<li>
										<v-contextual-option
											@click="viewFile(instruction.pdf)">View Case Summary PDF</v-contextual-option>
									</li>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('viewFactFindResponse', instruction)"
										@click="viewFactFindResponse()">View Fact Find</v-contextual-option>
								</ul>
								<ul>
									<v-contextual-option
										v-if="$can('orderProducts', instruction)"
										@click="openProductsModal(instruction)">Order Products</v-contextual-option>
								</ul>
							</v-contextual>
						</div>
					</div>
				</div>
				<div class="section section--flat mt-1">
					<dl class="flex gap-2">
						<div>
							<dt class="all-caps gamma">Assigned Team</dt>
							<dd v-if="!instruction.assigned_team" class="beta">—</dd>
							<dd v-else class="beta" v-html="instruction.assigned_team.name"/>
						</div>
						<div>
							<dt class="all-caps gamma">Assigned User</dt>
							<dd v-if="!instruction.assigned_user" class="beta">—</dd>
							<dd v-else class="beta" v-html="$present('fullName', instruction.assigned_user.profile)"/>
						</div>
					</dl>
				</div>
			</header>
		</div>
		<section
			v-if="instruction.caseProgress && $can('viewCaseMilestones', instruction)"
			class="section section--bordered">
			<header class="flex gap-2 content-between items-center">
				<h3 class="beta heavy">Case progress</h3>
				<button
					v-if="$can('updateCaseMilestones', instruction)"
					class="button button--primary--small"
					@click.prevent="showCaseMilestonesModal()">Update Case Milestones
				</button>
			</header>
			<div class="section section--flat mt-2">
				<dl class="instruction__case-progress gap-2">
					<div
						v-for="(milestone, milestoneIndex) in instruction.caseProgress"
						:key="milestoneIndex"
						:class="milestoneClass(milestone)"
						class="center instruction__case-progress__milestone">
						<dt>
							<span v-html="milestone.label"/>
						</dt>
						<dd>
							<dl>
								<dt class="sr-only">Status</dt>
								<dd
									v-if="!milestone.complete"
									class="sr-only">Pending
								</dd>
								<dd v-else class="sr-only">Complete</dd>
								<template v-if="milestone.value.date">
									<dt class="sr-only">Date</dt>
									<dd v-html="$present('date', milestone.value.date)"/>
								</template>
							</dl>
						</dd>
						<dd
							v-if="!milestone.complete"
							class="sr-only"
							v-html="!milestone.complete ? 'Incomplete' : 'Complete'"/>
					</div>
				</dl>
			</div>
		</section>
		<section
			v-if="instruction.clientContract || instruction.proofOfFunds || instruction.protocolPack || instruction.searchPack"
			class="section section--bordered">
			<header>
				<h3 class="beta heavy">Documents</h3>
			</header>
			<div class="sections mt-2">
				<article
					v-if="instruction.clientContract"
					class="section section--flexed section--flat service">
					<header class="flex gap-2 content-between items-center">
						<h4 class="beta heavy">Client care letter</h4>
						<div class="flex gap-1 content-between items-center">
							<ul class="actions actions--icons">
								<li>
									<button
										class="button--icon icon--view"
										@click="showClientContractModal(instruction)">View client care letter
									</button>
								</li>
								<li>
									<button
										class="button--icon icon--pdf"
										@click.prevent="viewFile(instruction.clientContract.pdfUri)">Open client care letter PDF
									</button>
								</li>
								<!--								<li>-->
								<!--									<button-->
								<!--										class="button&#45;&#45;icon icon&#45;&#45;download"-->
								<!--										@click.prevent="viewFile(instruction.clientContract.downloadPdfUri)">Download Client Contract PDF-->
								<!--									</button>-->
								<!--								</li>-->
							</ul>
							<status-badge
								:status="instruction.clientContract.status"
								group="client-contract"/>
						</div>
					</header>
				</article>
				<article
					v-if="instruction.proofOfFunds"
					class="section section--flexed section--flat service">
					<header class="flex gap-2 content-between items-center">
						<h4 class="beta heavy">Proof of funds</h4>
						<div class="flex gap-1 content-between items-center">
							<ul class="actions actions--icons">
								<li>
									<button
										class="button--icon icon--view"
										@click="showProofOfFundsModal(instruction)">View proof of funds
									</button>
								</li>
								<li>
									<button
										class="button--icon icon--download"
										@click.prevent>Download proof of funds
									</button>
								</li>
							</ul>
							<status-badge
								:status="instruction.proofOfFunds.status"
								group="proof-of-funds"/>
						</div>
					</header>
					<dl class="flex content-between gap-2 mt-2">
						<div>
							<dt class="all-caps gamma">Amount</dt>
							<dd
								class="alpha"
								v-html="$present('price', instruction.proofOfFunds.amount_of_funds)"/>
						</div>
						<progress-indicator
							:percentage="instruction.proofOfFunds.completionPercentage.percent"
							class="metrics__metric--progress--no-title metrics__metric--progress--small"/>
					</dl>
				</article>
				<article
					v-if="instruction.protocolPack"
					class="section section--flexed section--flat service">
					<header class="flex gap-2 content-between items-center">
						<h4 class="beta heavy">Protocol pack</h4>
						<div class="flex gap-1 content-between items-center">
							<ul class="actions actions--icons">
								<li>
									<button
										class="button--icon icon--view"
										@click="showProtocolPackModal(instruction)">View protocol pack
									</button>
								</li>
								<li>
									<button
										class="button--icon icon--download"
										@click.prevent="downloadFile(instruction.protocolPack.download, 'Protocol Pack.zip', instruction.protocolPack.id)">Download protocol pack
									</button>
								</li>
							</ul>
							<status-badge
								:status="instruction.protocolPack.status"
								group="protocol-pack"/>
						</div>
					</header>
					<dl class="flex content-between gap-2 mt-2">
						<div>
							<dt class="all-caps gamma">Forms</dt>
							<dd
								class="alpha"
								v-html="Object.keys(instruction.protocolPack.forms).length"/>
						</div>
						<progress-indicator
							:percentage="instruction.protocolPack.completionPercentage.percent"
							class="metrics__metric--progress--no-title metrics__metric--progress--small"/>
					</dl>
				</article>
				<article
					v-if="instruction.searchPack"
					class="section section--flexed section--flat service">
					<header class="flex gap-2 content-between items-center">
						<h4 class="beta heavy">Search pack</h4>
						<div class="flex gap-1 content-between items-center">
							<ul class="actions actions--icons">
								<li v-if="instruction.searchPack.file">
									<button
										class="button--icon icon--pdf"
										@click="viewFile(instruction.searchPack.file.uri)">View search pack PDF
									</button>
								</li>
								<li v-if="$can('uploadFile', instruction.searchPack)">
									<button
										class="button--icon icon--upload"
										@click.prevent="showSearchPackModal()">Upload search pack PDF
									</button>
								</li>
							</ul>
							<status-badge
								:status="instruction.searchPack.status"
								group="search-pack"/>
						</div>
					</header>
					<!--					<dl class="flex content-between gap-2 mt-2">-->
					<!--						<div>-->
					<!--							<dt class="all-caps gamma">Products</dt>-->
					<!--							<dd class="alpha">5</dd>-->
					<!--							&lt;!&ndash;									<dd v-html="instruction.searchPack.products.length"/>&ndash;&gt;-->
					<!--						</div>-->
					<!--						<progress-indicator-->
					<!--							:percentage="40"-->
					<!--							class="metrics__metric&#45;&#45;progress&#45;&#45;no-title metrics__metric&#45;&#45;progress&#45;&#45;small"/>-->
					<!--						&lt;!&ndash;						<progress-indicator&ndash;&gt;-->
					<!--						&lt;!&ndash;							:percentage="instruction.searchPack.completionPercentage.percent"&ndash;&gt;-->
					<!--						&lt;!&ndash;							class="metrics__metric&#45;&#45;progress&#45;&#45;no-title metrics__metric&#45;&#45;progress&#45;&#45;small"/>&ndash;&gt;-->
					<!--					</dl>-->
				</article>
			</div>
		</section>
		<!--		<section v-if="instruction.meta.length" class="section section&#45;&#45;bordered">-->
		<!--			<header>-->
		<!--				<h2 class="beta heavy">Meta</h2>-->
		<!--			</header>-->
		<!--			<dl class="details-list mt-2">-->
		<!--				<div v-for="(meta, metaIndex) in instruction.meta" class="detail-list__item">-->
		<!--					<dt v-html="stringer(meta.key).words().join(' ').value"/>-->
		<!--					<dd v-html="meta.value"/>-->
		<!--				</div>-->
		<!--			</dl>-->
		<!--		</section>-->
	</article>
</template>
<script>
import Address from "../../../../../components/Address.vue";
import QuoteBreakdownConveyancing from "../../../../../components/QuoteBreakdownConveyancing.vue";
import QuoteBreakdownSdlt from "../../../../../components/QuoteBreakdownSdlt.vue";
import opensFile from "../../../../../mixins/opensFile";
import Conveyancing from "./Types/Conveyancing.vue";
import Default from "./Types/Default.vue";

export default {
	components: {
		Address,
		QuoteBreakdownConveyancing,
		QuoteBreakdownSdlt,
		Conveyancing,
		Default,
	},

	mixins: [opensFile],

	data() {
		return {
			processing: false,
		};
	},

	computed: {
		instructionComponent() {
			return {
				conveyancing: Conveyancing,
			}[this.instruction.service.key] || Default;
		},
	},

	props: {
		client: {
			required: true,
			type: Object,
		},
		instruction: {
			required: true,
			type: Object,
		},
		// group: {
		// 	required: true,
		// 	type: Object,
		// },
	},

	methods: {
		instructionHeader(instruction) {
			if (!instruction.service_variant) {
				return instruction.service.names.singular;
			}
			return [
				instruction.service.names.singular,
				this.$present("ucWords", instruction.service_variant),
			].join(" | ");
		},
		milestoneClass(milestone) {
			let classes = {};
			classes["instruction__case-progress__milestone--" + milestone.key] = true;
			classes["instruction__case-progress__milestone--" + milestone.key + "--complete"] = milestone.value.complete;
			return classes;
		},
		showAcceptModal() {
			this.$vmodal.open({
				component: () => import("./modals/Accept.vue"),
				props: {
					instruction: this.instruction,
					group: this.group,
					instructionHeader: this.instructionHeader,
				},
			});
		},
		showAssignModal() {
			this.$vmodal.open({
				component: require("./modals/Assign.vue").default,
				props: {
					instruction: this.instruction,
					client: this.client,
					instructionHeader: this.instructionHeader,
				},
			});
		},
		showQuoteModal(service, quote) {
			let component = {
				conveyancing: QuoteBreakdownConveyancing,
				landTaxCalculation: QuoteBreakdownSdlt,
			}[service.key];
			this.$vmodal.open({
				component: component,
				props: {
					quote: quote,
				},
			});
		},
		showCaseMilestonesModal() {
			this.$vmodal.open({
				component: () => import("./modals/UpdateCaseMilestones.vue"),
				props: {
					instruction: this.instruction,
				},
			});
		},
		showClientContractModal() {
			this.$vmodal.open({
				component: () => import("./modals/ClientContract.vue"),
				props: {
					instruction: this.instruction,
				},
			});
		},
		showProofOfFundsModal() {
			this.$vmodal.open({
				component: () => import("./modals/ProofOfFunds.vue"),
				props: {
					instruction: this.instruction,
				},
			});
		},
		showProtocolPackModal(instruction) {
			this.$vmodal.open({
				component: () => import("@/apps/portal/views/client/instruction/modals/ProtocolPack.vue"),
				props: {
					instruction: instruction,
					// protocolPack: this.instruction.protocolPack,
					// clients: this.instruction.clients,
				},
			});
		},
		showSearchPackModal() {
			this.$vmodal.open({
				component: () => import("./modals/SearchPack.vue"),
				props: {
					instruction: this.instruction,
				},
			});
		},
		openProductsModal(instruction) {
			this.$vmodal.open({
				component: () => import("./modals/OrderProducts.vue"),
				props: {
					client: this.client,
					instruction: instruction,
				},
			});
		},
		redirectToWillSuite() {
			window.open("https://client.willsuite.co.uk/login", "_blank");
		},
		showPushToExternalServiceModal() {
			this.$vmodal.open({
				component: () => import("./modals/PushToExternalService.vue"),
				props: {
					instruction: this.instruction,
				},
			});
		},
		complete() {
			http().patch(this.route("instructions/:instruction/complete", this.instruction)).then(response => {
				this.$store.commit("instructions/UPDATE_ITEM", response.data.data);
			});
		},
		displayQuoteLinkModal(sendEmail = false) {
			this.$vmodal.open({
				component: () => import("../modals/QuoteLink.vue"),
				props: {
					instruction: this.instruction,
					client: this.client,
					shouldSendEmail: sendEmail,
				},
			});
		},
	},
};
</script>
